/**
 * LE MUSEE: on y dessine au doigt, et on y regarde ce que les autres ont
 * accroche.
 *
 * DEUX ECRANS, UN SEUL PANNEAU. La galerie (ce qui est accroche) et le
 * chevalet (ce qu'on est en train de faire) sont deux etats du meme panneau et
 * non deux panneaux: ils partagent le meme fond, la meme fermeture, la meme
 * barriere a raccourcis, et surtout le joueur passe de l'un a l'autre sans que
 * rien ne se ferme sous ses yeux.
 *
 * TOUT EST EN 2D, DANS UN CANVAS. Rien de tout cela ne touche a three.js:
 * accrocher de vraies toiles peintes sur les murs du musee demanderait des
 * TEXTURES, et le projet n'en a aucune — tout y est procedural, sans un seul
 * fichier d'image. Un canvas de galerie coute zero appel de dessin dans la
 * scene et se relit sur un telephone.
 *
 * LES COORDONNEES NE SONT PAS DES PIXELS. Le doigt est converti dans la grille
 * de `shared/sim/dessin.ts` (0 a 1000) des l'evenement, et c'est cette grille
 * qui voyage et qui est stockee: un dessin fait sur un telephone de 390 points
 * de large se rouvre tel quel sur un ecran de bureau. C'est aussi ce qui rend
 * le filtrage des points (`SEUIL_POINT`) independant de l'ecran.
 */
import { Panneau, bouton, el, hex, refusSonore } from './commun.ts'
import {
  COTE_DESSIN,
  EPAISSEURS,
  MAX_POINTS_DESSIN,
  MAX_TITRE,
  MAX_TRAITS,
  PALETTE,
  ajouterPoint,
  dessinAccrochable,
  nombrePoints,
  type Dessin,
  type Trait,
} from '../../shared/sim/dessin.ts'
import { couleurCompte, estAdmin } from '../../shared/data/proprietaires.ts'
import { journal } from '../log/journal.ts'

/** Ce que le panneau demande au jeu: lire la galerie et y accrocher. */
export interface RappelsMusee {
  /** Lit les dessins accroches, ALLEGES pour les vignettes. Vide en cas de panne. */
  lire(): Promise<Dessin[]>
  /** Lit UN dessin avec tous ses points, pour le voir en grand. */
  lireUn(id: string): Promise<Dessin | null>
  /**
   * Accroche un dessin. Rend celui que le SERVEUR a ecrit, et la raison quand
   * il refuse — un invite doit lire « il faut un compte », pas « reessaie ».
   */
  accrocher(titre: string, traits: Trait[]): Promise<{ dessin: Dessin | null; raison: string }>
  /** Decroche un dessin. Rend vrai si le serveur l'a retire. */
  decrocher(id: string): Promise<boolean>
  /** Nom de COMPTE du joueur, ou null en invite. Sert a proposer le decrochage. */
  compte(): string | null
  /** Previent le joueur. */
  message(texte: string, duree: number, genre: 'info' | 'succes' | 'erreur'): void
}

/**
 * Dessine un dessin dans un contexte 2D, ajuste a un carre de `cote` pixels.
 *
 * Exportee parce que la bibliotheque s'en sert aussi pour ses cases de bande
 * dessinee: c'est le meme format de traits, et deux fonctions pour tracer les
 * memes points finiraient par diverger — celle qui n'est pas utilisee tous les
 * jours etant celle qui pourrit.
 */
export function tracerDessin(ctx: CanvasRenderingContext2D, traits: readonly Trait[], cote: number): void {
  const k = cote / COTE_DESSIN
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  for (const t of traits) {
    if (t.points.length < 2) continue
    ctx.strokeStyle = hex(t.couleur)
    ctx.lineWidth = Math.max(1, t.epaisseur * k)
    ctx.beginPath()
    ctx.moveTo((t.points[0] ?? 0) * k, (t.points[1] ?? 0) * k)
    if (t.points.length === 2) {
      // UN SEUL POINT: un `stroke` sur un chemin sans longueur ne dessine rien
      // avec certains navigateurs. On pose donc un point franc, sinon taper une
      // fois sur la toile ne laissait aucune trace et paraissait casse.
      ctx.lineTo((t.points[0] ?? 0) * k + 0.01, (t.points[1] ?? 0) * k)
    } else {
      /*
       * ON RELIE LES POINTS PAR DES COURBES, PAS PAR DES SEGMENTS.
       *
       * Un doigt ne trace pas des droites, et une suite de `lineTo` en fait
       * pourtant: chaque point devient un ANGLE, et une boucle dessinee
       * lentement revient en polygone — d'autant plus visiblement que le trait
       * a peu de points (une vignette allegee, un geste rapide). Le remede est
       * le classique du dessin au doigt: chaque point est le POINT DE CONTROLE
       * d'une quadratique, et la courbe passe par les MILIEUX. Le trait garde
       * sa direction en entrant et en sortant de chaque point, donc il tourne
       * au lieu de casser.
       *
       * Ca ne change rien a ce qui est stocke: la donnee reste la suite de
       * points du doigt, et c'est la meme fonction qui trace le chevalet, la
       * galerie, le grand format et les cases de bande dessinee — on voit
       * exactement ce qu'on a dessine.
       */
      const n = t.points.length / 2
      for (let i = 1; i < n - 1; i++) {
        const cx = (t.points[i * 2] ?? 0) * k
        const cz = (t.points[i * 2 + 1] ?? 0) * k
        const mx = (cx + (t.points[(i + 1) * 2] ?? 0) * k) / 2
        const mz = (cz + (t.points[(i + 1) * 2 + 1] ?? 0) * k) / 2
        ctx.quadraticCurveTo(cx, cz, mx, mz)
      }
      // Le DERNIER point, en droite ligne: c'est lui qui ferme la forme, et une
      // courbe de plus le depasserait.
      ctx.lineTo((t.points[(n - 1) * 2] ?? 0) * k, (t.points[(n - 1) * 2 + 1] ?? 0) * k)
    }
    ctx.stroke()
  }
}

/** Une vignette de galerie: le dessin, son titre, son auteur. */
function vignette(d: Dessin, cote: number, onClic: () => void): HTMLElement {
  const carte = el('button', 'galerie__carte')
  carte.type = 'button'
  const toile = el('canvas', 'galerie__toile')
  toile.width = cote
  toile.height = cote
  const ctx = toile.getContext('2d')
  if (ctx) {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, cote, cote)
    tracerDessin(ctx, d.traits, cote)
  }
  const legende = el('span', 'galerie__legende', d.titre || 'Sans titre')
  const auteur = el('span', 'galerie__auteur', d.auteur || 'anonyme')
  auteur.style.color = hex(couleurCompte(d.auteur))
  carte.append(toile, legende, auteur)
  carte.addEventListener('click', onClic)
  return carte
}

export class PanneauMusee extends Panneau {
  private readonly rappels: RappelsMusee

  /** Le chevalet. */
  private toile: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  private traits: Trait[] = []
  private enCours: Trait | null = null
  private couleur = PALETTE[0] ?? 0x1a1a1a
  private epaisseur = EPAISSEURS[1] ?? 14
  private titre = ''
  /** Pointeur qui trace en ce moment. Un seul: on ne dessine pas a deux doigts. */
  private pointeur: number | null = null

  constructor(parent: HTMLElement, rappels: RappelsMusee) {
    // Deux classes: `--large` porte la mise en page (les deux panneaux qui
    // montrent des images sont plus larges que les autres), `--musee` porte son
    // identite — sa teinte, et de quoi le designer sans ambiguite. Les deux
    // panneaux ne partageaient au depart que `--large`, et plus rien ne les
    // distinguait dans le DOM.
    super(parent, 'musee', 'Musée', '🖼️', 'panneau-fond--large panneau-fond--musee')
    this.rappels = rappels
  }

  /** Ouvre le musee sur sa galerie. */
  async ouvrir(): Promise<void> {
    journal.input('musee', 'panneau ouvert')
    this.montrerGalerie()
    this.afficher('galerie')
    await this.rafraichirGalerie()
  }

  // ------------------------------------------------------------- la galerie

  private montrerGalerie(): void {
    /*
     * On OUBLIE le chevalet ici, pas seulement dans `retourGalerie`.
     *
     * `rafraichirGalerie` refuse d'ecraser le corps du panneau tant qu'une
     * toile existe — c'est ce qui empeche la reponse du reseau d'effacer le
     * dessin qu'on vient de commencer. Fermer le panneau en plein dessin
     * laissait donc `toile` renseignee, et la galerie restait bloquee sur « on
     * decroche les toiles… » a la reouverture suivante. Un garde-fou pose a un
     * seul des deux endroits n'en est pas un.
     */
    this.toile = null
    this.ctx = null
    this.majEntete('Musée', '🖼️')
    this.viderCorps()
    this.viderPied()
    this.corps.append(el('p', 'musee__attente', 'On décroche les toiles…'))
    this.pied.append(bouton('🎨 Dessiner', () => this.montrerChevalet(), 'bouton--primaire'))
  }

  private async rafraichirGalerie(): Promise<void> {
    const dessins = await this.rappels.lire()
    // Le joueur a pu fermer, ou passer au chevalet, pendant l'aller-retour: on
    // ne remplace alors rien. Sans ce garde-fou, la reponse du reseau effacait
    // le dessin qu'on venait de commencer.
    if (!this.ouvert || this.toile) return
    this.viderCorps()
    if (dessins.length === 0) {
      this.corps.append(
        el('p', 'musee__vide', 'Les murs sont encore nus. Sois le premier à accrocher quelque chose !'),
      )
      return
    }
    const grille = el('div', 'galerie')
    for (const d of dessins) grille.append(vignette(d, 220, () => void this.montrerEnGrand(d)))
    this.corps.append(grille)
    journal.info('musee', `${dessins.length} dessins accroches`)
  }

  /**
   * Ouvre une toile en grand, et va chercher SES POINTS.
   *
   * Ce que la galerie a recu est allege pour des vignettes de 220 pixels; a 560
   * pixels, les angles se verraient. On affiche donc tout de suite la version
   * connue — pas d'ecran blanc — puis on redessine par-dessus quand la version
   * entiere arrive.
   */
  private async montrerEnGrand(d: Dessin): Promise<void> {
    journal.input('musee', `regarde « ${d.titre || 'sans titre'} » de ${d.auteur}`)
    this.majEntete(d.titre || 'Sans titre', '🖼️')
    this.viderCorps()
    this.viderPied()
    const cote = 560
    const toile = el('canvas', 'musee__grand')
    toile.width = cote
    toile.height = cote
    const ctx = toile.getContext('2d')
    const peindre = (traits: readonly Trait[]): void => {
      if (!ctx) return
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, cote, cote)
      tracerDessin(ctx, traits, cote)
    }
    peindre(d.traits)
    const signature = el('p', 'musee__signature', `par ${d.auteur || 'anonyme'}`)
    signature.style.color = hex(couleurCompte(d.auteur))
    this.corps.append(toile, signature)
    this.pied.append(bouton('← La galerie', () => void this.retourGalerie()))
    // On ne decroche que SA propre toile, et le bouton n'apparait que la: le
    // serveur refuserait de toute facon (la clause est dans le DELETE), mais un
    // bouton qui echoue toujours est pire qu'un bouton absent.
    if (this.peutDecrocher(d)) {
      this.pied.append(bouton('🗑 Décrocher', () => void this.decrocher(d)))
    }

    const entier = await this.rappels.lireUn(d.id)
    // Le joueur a pu revenir a la galerie pendant l'aller-retour: on ne peint
    // que si la toile est encore a l'ecran.
    if (entier && toile.isConnected) peindre(entier.traits)
  }

  /**
   * Qui peut decrocher cette toile: son auteur, et l'administrateur.
   *
   * L'ADMINISTRATEUR EN A BESOIN. Le musee est partage et un enfant peut y
   * accrocher n'importe quoi; sans ce droit, la seule facon de retirer un
   * dessin genant etait d'effacer le monde entier. Le bouton n'apparait que
   * pour qui a le droit — le serveur refuserait de toute facon, mais un bouton
   * qui echoue toujours est pire qu'un bouton absent.
   */
  private peutDecrocher(d: Dessin): boolean {
    // `aMoi` vient du SERVEUR, qui compare les jetons: c'est la seule reponse
    // juste pour un invite, qui signe « invité » comme tous les invites.
    if (d.aMoi === true) return true
    const moi = this.rappels.compte()
    return moi !== null && estAdmin(moi)
  }

  private async decrocher(d: Dessin): Promise<void> {
    journal.input('musee', `decroche « ${d.titre || 'sans titre'} »`, { id: d.id })
    if (!(await this.rappels.decrocher(d.id))) {
      this.rappels.message('Le musée n’a pas pu le décrocher.', 3000, 'erreur')
      refusSonore()
      return
    }
    this.rappels.message('Décroché.', 2000, 'info')
    await this.retourGalerie()
  }

  private async retourGalerie(): Promise<void> {
    this.montrerGalerie()
    await this.rafraichirGalerie()
  }

  // ------------------------------------------------------------ le chevalet

  private montrerChevalet(): void {
    journal.input('musee', 'chevalet ouvert')
    this.traits = []
    this.enCours = null
    this.titre = ''
    this.majEntete('Dessine', '🎨')
    this.viderCorps()
    this.viderPied()

    const toile = el('canvas', 'chevalet__toile')
    // La toile est CARREE en pixels et la grille l'est aussi: c'est ce qui rend
    // la conversion doigt -> grille une simple division, sans deformation.
    toile.width = COTE_DESSIN
    toile.height = COTE_DESSIN
    this.toile = toile
    this.ctx = toile.getContext('2d')
    this.effacerToile()

    toile.addEventListener('pointerdown', this.surPointeurBas)
    toile.addEventListener('pointermove', this.surPointeurBouge)
    toile.addEventListener('pointerup', this.surPointeurHaut)
    toile.addEventListener('pointercancel', this.surPointeurHaut)

    // Le TITRE en tete, au-dessus de la toile. Il etait sous la palette et les
    // epaisseurs, c'est-a-dire SOUS LE PIED du panneau sur un telephone: il
    // fallait faire defiler pour le trouver, et la galerie s'est remplie de
    // « Sans titre » (onze toiles sur treize, vu au telephone le 14 octobre).
    // En tete, le clavier systeme ne le couvre pas non plus.
    this.corps.append(this.champTitre(), toile, this.barreOutils())
    this.pied.append(
      bouton('↶ Annuler', () => this.annulerTrait()),
      bouton('🗑 Tout effacer', () => this.toutEffacer()),
      bouton('🖼️ Accrocher', () => void this.accrocher(), 'bouton--primaire'),
      bouton('← La galerie', () => void this.retourGalerie()),
    )
  }

  /** Palette et epaisseurs. */
  private barreOutils(): HTMLElement {
    const barre = el('div', 'chevalet__outils')

    const couleurs = el('div', 'chevalet__palette')
    const pastilles: HTMLButtonElement[] = []
    for (const c of PALETTE) {
      const p = el('button', 'pastille')
      p.type = 'button'
      p.style.background = hex(c)
      p.setAttribute('aria-label', `Couleur ${hex(c)}`)
      if (c === this.couleur) p.classList.add('pastille--active')
      p.addEventListener('click', () => {
        this.couleur = c
        for (const autre of pastilles) autre.classList.remove('pastille--active')
        p.classList.add('pastille--active')
        journal.input('musee', `couleur ${hex(c)}`)
      })
      pastilles.push(p)
      couleurs.append(p)
    }

    const tailles = el('div', 'chevalet__epaisseurs')
    const boutons: HTMLButtonElement[] = []
    for (const e of EPAISSEURS) {
      const b = el('button', 'epaisseur')
      b.type = 'button'
      b.setAttribute('aria-label', `Épaisseur ${e}`)
      const rond = el('span', 'epaisseur__rond')
      // Le rond montre l'epaisseur REELLE, ramenee a la taille du bouton: un
      // libelle « 30 » ne veut rien dire pour un enfant, un gros point si.
      const taille = Math.max(4, Math.round((e / (EPAISSEURS[EPAISSEURS.length - 1] ?? 60)) * 26))
      rond.style.width = `${taille}px`
      rond.style.height = `${taille}px`
      b.append(rond)
      if (e === this.epaisseur) b.classList.add('epaisseur--active')
      b.addEventListener('click', () => {
        this.epaisseur = e
        for (const autre of boutons) autre.classList.remove('epaisseur--active')
        b.classList.add('epaisseur--active')
      })
      boutons.push(b)
      tailles.append(b)
    }

    barre.append(couleurs, tailles)
    return barre
  }

  private champTitre(): HTMLElement {
    /*
     * LE TITRE EST UN CHAMP DE SAISIE, et c'est une exception assumee.
     *
     * Le panneau de l'ecole n'en a aucun, a dessein: sur un telephone le
     * clavier systeme mange la moitie de l'ecran. Ici, il n'y a pas d'autre
     * facon de nommer son dessin — un pave de boutons ne donne pas des mots —
     * et le champ ne sert qu'une fois, avant d'accrocher. Il porte la MEME
     * barriere que le chat, sans quoi ecrire « avale » monterait en voiture et
     * allumerait la lampe.
     */
    const ligne = el('label', 'chevalet__titre')
    ligne.append(el('span', 'chevalet__titre-texte', 'Titre'))
    const champ = el('input')
    champ.type = 'text'
    champ.maxLength = MAX_TITRE
    champ.placeholder = 'Sans titre'
    champ.className = 'chevalet__champ'
    champ.addEventListener('input', () => {
      this.titre = champ.value
    })
    champ.addEventListener('keydown', (e) => {
      // BARRIERE. Les raccourcis du jeu ecoutent la FENETRE sans regarder ou va
      // la frappe: ce qu'on ecrit dans le champ reste dans le champ.
      e.stopPropagation()
      if (e.key === 'Escape') {
        e.preventDefault()
        champ.blur()
      }
    })
    ligne.append(champ)
    return ligne
  }

  private effacerToile(): void {
    const ctx = this.ctx
    if (!ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, COTE_DESSIN, COTE_DESSIN)
  }

  /** Redessine tout: apres une annulation, il n'y a pas d'autre moyen honnete. */
  private redessiner(): void {
    const ctx = this.ctx
    if (!ctx) return
    this.effacerToile()
    tracerDessin(ctx, this.traits, COTE_DESSIN)
    if (this.enCours) tracerDessin(ctx, [this.enCours], COTE_DESSIN)
  }

  /** Position du doigt dans la GRILLE, pas en pixels. */
  private surLaGrille(e: PointerEvent): { x: number; z: number } | null {
    const toile = this.toile
    if (!toile) return null
    const r = toile.getBoundingClientRect()
    if (r.width < 1 || r.height < 1) return null
    return {
      x: ((e.clientX - r.left) / r.width) * COTE_DESSIN,
      z: ((e.clientY - r.top) / r.height) * COTE_DESSIN,
    }
  }

  private readonly surPointeurBas = (e: PointerEvent): void => {
    // UN SEUL DOIGT trace. Sans ce verrou, un pincement a deux doigts (le geste
    // de zoom du jeu, que les enfants font par reflexe) tracait deux traits en
    // meme temps et melangeait leurs points.
    if (this.pointeur !== null) return
    if (this.traits.length >= MAX_TRAITS) {
      this.rappels.message('Ce dessin est déjà bien chargé : accroche-le !', 2600, 'info')
      refusSonore()
      return
    }
    const p = this.surLaGrille(e)
    if (!p) return
    e.preventDefault()
    this.pointeur = e.pointerId
    this.toile?.setPointerCapture(e.pointerId)
    this.enCours = { couleur: this.couleur, epaisseur: this.epaisseur, points: [] }
    ajouterPoint(this.enCours, p.x, p.z)
    this.redessiner()
  }

  private readonly surPointeurBouge = (e: PointerEvent): void => {
    if (this.pointeur !== e.pointerId || !this.enCours) return
    const p = this.surLaGrille(e)
    if (!p) return
    e.preventDefault()
    if (ajouterPoint(this.enCours, p.x, p.z)) this.redessiner()
  }

  private readonly surPointeurHaut = (e: PointerEvent): void => {
    if (this.pointeur !== e.pointerId) return
    this.pointeur = null
    const t = this.enCours
    this.enCours = null
    if (!t || t.points.length < 2) return
    this.traits.push(t)
    // Le plafond global est verifie ICI et pas seulement au moment d'accrocher:
    // laisser dessiner puis tout refuser a la fin serait cruel.
    if (nombrePoints({ id: '', auteur: '', titre: '', traits: this.traits, creeA: 0 }) >= MAX_POINTS_DESSIN) {
      this.rappels.message('La toile est pleine. Accroche-la ou efface un peu.', 3000, 'info')
    }
    this.redessiner()
  }

  private annulerTrait(): void {
    if (this.traits.length === 0) return
    this.traits.pop()
    this.redessiner()
    journal.input('musee', 'trait annule', { restants: this.traits.length })
  }

  private toutEffacer(): void {
    this.traits = []
    this.enCours = null
    this.redessiner()
    journal.input('musee', 'toile effacee')
  }

  private async accrocher(): Promise<void> {
    const brouillon: Dessin = { id: '', auteur: '', titre: this.titre, traits: this.traits, creeA: 0 }
    if (!dessinAccrochable(brouillon)) {
      this.rappels.message('Dessine encore un peu avant de l’accrocher.', 2600, 'erreur')
      refusSonore()
      return
    }
    journal.input('musee', `accroche « ${this.titre || 'sans titre'} »`, {
      traits: this.traits.length,
      points: nombrePoints(brouillon),
    })
    const { dessin: ecrit, raison } = await this.rappels.accrocher(this.titre, this.traits)
    if (!ecrit) {
      // La phrase du serveur d'abord: elle dit ce qui manque vraiment.
      this.rappels.message(raison || 'Le musée n’a pas pu l’accrocher. Réessaie.', 4000, 'erreur')
      refusSonore()
      return
    }
    this.rappels.message('🖼️ Ton dessin est accroché au musée !', 3600, 'succes')
    await this.retourGalerie()
  }
}
